import { Button } from "@/components/ui/button";
import { ArrowRight, Award, Users, Smartphone } from "lucide-react";
import heroImage from "@/assets/hero-music-education.jpg";
import badgeGooglePlay from "@/assets/badge-google-play.png";
import badgeAppStore from "@/assets/badge-app-store.svg";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";
import { FACHKRAEFTE_KURZ, LEKTIONEN_TEXT, SEIT_TEXT, gross } from "@/config/webseiteFakten";

const HeroSection = () => {
  const isNativeApp = useIsNativeApp();
  return (
    <section className="relative pt-32 pb-0 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-hero-gradient-start via-background to-hero-gradient-end opacity-60" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="inline-block px-4 py-2 bg-primary/10 rounded-full">
              <span className="text-sm font-medium text-primary">✨ Jetzt mit KI-Unterstützung durch Miko</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight">
              Ideen für mehr{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                Musik mit Kindern
              </span>
            </h1>
            
            <div className="text-base text-muted-foreground max-w-lg leading-relaxed space-y-4">
              <p>
                MusiKonzept unterstützt dich dabei, Musik ganz leicht in deinen Alltag mit Kindern zu bringen.
              </p>
              <p>
                Bei uns findest du praxisnahe Liedideen, kreative Impulse, Webinare, eine große Online-Akademie und viele sofort umsetzbare Materialien. Alles wurde speziell für pädagogische Fachkräfte und Einrichtungen entwickelt – damit du ohne lange Vorbereitung direkt loslegen kannst.
              </p>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105" asChild>
                <a href="/registrieren">
                  Jetzt entdecken
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-8 border-2 hover:bg-primary/10 hover:text-primary hover:border-primary transition-all duration-300" asChild>
                <a href="/kurse">
                  Alle Inhalte entdecken
                </a>
              </Button>
            </div>
            
          </div>

          {/* Right Image */}
          <div 
            className="relative animate-fade-in group/hero" 
            style={{ animationDelay: "0.2s" }}
          >
            {/* Sehr sanfter, pulsierender Background Glow - keine Bewegung */}
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 rounded-3xl blur-3xl opacity-100 group-hover/hero:opacity-100 transition-opacity duration-[2000ms] animate-[pulse_8s_ease-in-out_infinite]" 
                 style={{ animationTimingFunction: 'ease-in-out' }} />
            
            <div className="relative rounded-3xl overflow-hidden shadow-2xl transition-shadow duration-700 hover:shadow-[0_25px_70px_-15px_rgba(var(--primary),0.3)]">
              <img 
                src={heroImage} 
                alt="Musikpädagogin mit Kindern beim gemeinsamen Musizieren" 
                className="w-full h-auto object-cover"
                width={760}
                height={420}
                fetchPriority="high"
                decoding="async"
              />
              {/* Decorative Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
              
              {/* Sehr sanfter Ring Border */}
              <div className="absolute inset-0 rounded-3xl ring-1 ring-primary/10 transition-all duration-1000" />
            </div>

            
            {/* Floating Badge - keine Bewegung, nur sanfter Glow */}
            <div 
              className="hidden lg:block absolute -bottom-6 -left-6 bg-card/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 border-2 border-primary/10 hover:border-primary/20 transition-all duration-500 cursor-pointer group/badge" 
              style={{ animationDelay: "0.4s" }}
            >
              {/* Sehr sanfter pulsierender Glow - nur Opacity-Änderung */}
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 rounded-2xl blur-xl opacity-100 transition-opacity duration-[2000ms] animate-[pulse_6s_ease-in-out_infinite]" />
              
              <div className="flex items-center gap-3 relative z-10">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center transition-transform duration-300 hover:scale-105">
                  <span className="text-2xl">🎵</span>
                </div>
                <div>
                  <div className="text-sm font-bold text-foreground transition-colors duration-300">{gross(LEKTIONEN_TEXT)}</div>
                  <div className="text-xs text-muted-foreground">Jederzeit verfügbar</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Proof + App Banner Row */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 mt-4 pb-8 relative z-20 lg:items-end">
          {/* Social Proof - left column */}
          <div className="flex flex-wrap gap-8 items-center">
            {/* Keine Bewertung ohne Quelle (Frage 297); Zahlen aus webseiteFakten.ts (Frage 293) */}
            <div className="flex items-center gap-2 text-sm">
              <Award className="w-5 h-5 text-primary" />
              <span className="font-medium text-foreground">{gross(SEIT_TEXT)}</span>
              <span className="text-muted-foreground">Musik mit Kindern</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-5 h-5 text-primary" />
              <span className="font-medium text-foreground">{FACHKRAEFTE_KURZ}</span>
              <span className="text-muted-foreground">Fachkräfte</span>
            </div>
          </div>

          {/* App Banner - right column */}
          {!isNativeApp && (
            <div className="animate-fade-in" style={{ animationDelay: "0.5s" }}>
              <div className="relative bg-card/80 backdrop-blur-sm border border-primary/15 rounded-2xl p-3 sm:p-4 shadow-lg overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary" />
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      Neu: MusiKonzept als App!
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Alle Inhalte immer dabei – kostenlos laden.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href="https://apps.apple.com/de/app/musikonzept/id6755689687" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 hover:scale-105 transition-all duration-200">
                      <img src={badgeAppStore} alt="Laden im App Store" className="h-[28px] sm:h-[32px] w-auto" />
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.wnapp.id1762014785410" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 hover:scale-105 transition-all duration-200">
                      <img src={badgeGooglePlay} alt="Jetzt bei Google Play" className="h-[28px] sm:h-[32px] w-auto" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
